import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META } from "@/components/StatusBadge";
import { exportCSV } from "@/lib/exports";
import { downloadReportPDF } from "@/lib/reportPdf";
import usePageTitle from "@/hooks/use-page-title";
import { FileText, Download, RefreshCw } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/date";

const REPORT_TYPES = [
  { value: "orders", label: "Orders Report" },
  { value: "payments", label: "Payments Report" },
  { value: "invoices", label: "Invoices Report" },
];

export default function Reports() {
  usePageTitle("Reports");
  const { user } = useAuth();
  const allowed = ["admin", "super_admin", "accountant"];

  const [type, setType] = useState("orders");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/customers", { params: { include_archived: true } }).then((r) => setCustomers(r.data));
  }, []);

  const run = async () => {
    setErr("");
    setLoading(true);
    try {
      const params = {};
      if (customerId) params.customer_id = customerId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (type === "orders" && status) params.status = status;
      const { data } = await api.get(`/${type}`, { params });
      setRows(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
     
  }, [type]);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const columns = useMemo(() => {
    if (type === "orders") {
      return {
        headers: ["Order ID", "Date", "Customer", "Product", "Qty", "Amount", "Paid", "Balance", "Status"],
        widths: [90, 80, 120, 130, 40, 70, 60, 60, 90],
        map: (o) => [
          o.id,
          fmtDate(o.created_at),
          o.customer_name,
          o.product_name,
          o.quantity,
          `₹${Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}`,
          `₹${Math.round(o.paid || 0).toLocaleString("en-IN")}`,
          `₹${Math.round(o.balance || 0).toLocaleString("en-IN")}`,
          STATUS_META[o.status]?.label || o.status,
        ],
        csv: (o) => ({
          OrderID: o.id, Date: o.created_at, Customer: o.customer_name, Company: o.customer_company,
          Product: o.product_name, Category: o.product_category, Qty: o.quantity,
          Amount: o.estimated_cost, Paid: o.paid, Balance: o.balance, Status: o.status,
        }),
      };
    }
    if (type === "payments") {
      return {
        headers: ["Date", "Order", "Customer", "Mode", "Amount", "Note"],
        widths: [90, 100, 160, 70, 90, 200],
        map: (p) => [
          fmtDate(p.created_at),
          p.order_id,
          p.customer_name,
          p.mode.toUpperCase(),
          `₹${Math.round(p.amount).toLocaleString("en-IN")}`,
          p.note || "",
        ],
        csv: (p) => ({
          Date: p.created_at, OrderID: p.order_id, Customer: p.customer_name,
          Mode: p.mode, Amount: p.amount, Note: p.note,
        }),
      };
    }
    // invoices
    return {
      headers: ["Invoice #", "Date", "Customer", "Total", "Paid", "Balance", "Status"],
      widths: [130, 80, 160, 90, 80, 90, 100],
      map: (o) => [
        `INV-${o.id}`,
        fmtDate(o.created_at),
        o.customer_name,
        `₹${Math.round(o.estimated_cost || 0).toLocaleString("en-IN")}`,
        `₹${Math.round(o.paid || 0).toLocaleString("en-IN")}`,
        `₹${Math.round(o.balance || 0).toLocaleString("en-IN")}`,
        STATUS_META[o.status]?.label || o.status,
      ],
      csv: (o) => ({
        InvoiceNo: `INV-${o.id}`, Date: o.created_at, Customer: o.customer_name,
        Total: o.estimated_cost, Paid: o.paid, Balance: o.balance, Status: o.status,
      }),
    };
  }, [type]);

  // Totals summary for finance
  const totals = useMemo(() => {
    if (type === "payments") {
      const total = rows.reduce((s, p) => s + (p.amount || 0), 0);
      return [["Total Payments", `₹${Math.round(total).toLocaleString("en-IN")}`], ["Count", rows.length]];
    }
    const total = rows.reduce((s, o) => s + (o.estimated_cost || 0), 0);
    const paid = rows.reduce((s, o) => s + (o.paid || 0), 0);
    const balance = rows.reduce((s, o) => s + (o.balance || 0), 0);
    return [
      [type === "invoices" ? "Total Invoiced" : "Total Value", `₹${Math.round(total).toLocaleString("en-IN")}`],
      ["Paid", `₹${Math.round(paid).toLocaleString("en-IN")}`],
      ["Outstanding", `₹${Math.round(balance).toLocaleString("en-IN")}`],
      ["Count", rows.length],
    ];
  }, [rows, type]);

  if (user && !allowed.includes(user.role)) return <Navigate to="/dashboard" replace />;

  const subtitle = [
    selectedCustomer ? `Customer: ${selectedCustomer.name}` : "All customers",
    dateFrom ? `From ${dateFrom}` : "",
    dateTo ? `To ${dateTo}` : "",
    type === "orders" && status ? `Status: ${STATUS_META[status]?.label || status}` : "",
  ].filter(Boolean).join("  ·  ");

  const activeReportLabel = REPORT_TYPES.find((r) => r.value === type)?.label || type;

  const downloadCSV = () => exportCSV(activeReportLabel.replace(/\s+/g, "-").toLowerCase(), rows.map(columns.csv));
  const downloadPDF = () =>
    downloadReportPDF({
      title: activeReportLabel,
      subtitle,
      headers: columns.headers,
      rows: rows.map(columns.map),
      columnWidths: columns.widths,
      totals: totals.map(([k, v]) => [k, String(v)]),
    });

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Reports</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Customised Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Filter by customer, date range and status. Export as CSV or PDF for GST filings, audits or customer statements.
          </p>
        </div>
        <FileText size={44} className="text-[#FF6B00]" />
      </div>

      {/* Filters */}
      <div className="card-swiss p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="overline block mb-2">Report Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
            data-testid="report-type-select"
          >
            {REPORT_TYPES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Customer</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
            data-testid="report-customer-select"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.company ? ` — ${c.company}` : ""}{c.is_deleted ? " (archived)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
            data-testid="report-from-input"
          />
        </div>
        <div>
          <label className="overline block mb-2">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
            data-testid="report-to-input"
          />
        </div>
        {type === "orders" && (
          <div>
            <label className="overline block mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
              data-testid="report-status-select"
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={run} disabled={loading} className="btn-swiss btn-accent" data-testid="report-run-btn">
          <RefreshCw size={14} /> {loading ? "Loading…" : "Apply Filters"}
        </button>
        <button onClick={downloadCSV} disabled={!rows.length} className="btn-swiss btn-outline" data-testid="report-csv-btn">
          <Download size={14} /> Download CSV
        </button>
        <button onClick={downloadPDF} disabled={!rows.length} className="btn-swiss btn-outline" data-testid="report-pdf-btn">
          <Download size={14} /> Download PDF
        </button>
        <div className="ml-auto text-sm text-muted-foreground">
          {rows.length} record{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="report-error">{err}</div>}

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {totals.map(([k, v]) => (
          <div key={k} className="card-swiss p-4">
            <div className="overline">{k}</div>
            <div className="font-display font-black text-2xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      {/* Preview */}
      <div className="card-swiss overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              {columns.headers.map((h) => (
                <th key={h} className="px-4 py-3 text-left overline whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || r.order_id || `row-${i}`} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                {columns.map(r).map((cell, j) => (
                  <td key={j} className="px-4 py-3 whitespace-nowrap">
                    {j === 0 ? <span className="font-mono text-xs">{cell}</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={columns.headers.length} className="px-4 py-12 text-center text-muted-foreground">
                  No records match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
